// Default config and business shapes (API-only; no mock persistence).

/** Display name used when business name is missing (e.g. welcome message fallback in store on 404). */
export const FALLBACK_BUSINESS_NAME = 'My Business';

/** Shape used when business is not loaded yet (e.g. config panel before fetch). */
const EMPTY_BUSINESS = { id: '', name: '' };

/** Placeholder in welcome message template; replaced with actual business name in store (404 merge). */
const WELCOME_MESSAGE_PLACEHOLDER = '{{businessName}}';

/**
 * Default welcome message template. Use getDefaultWelcomeMessage(businessName) to get the message
 * with business name substituted (e.g. in store when building config on 404).
 */
const DEFAULT_WELCOME_MESSAGE_TEMPLATE = `Hi,
Welcome to {{businessName}}.
How can I help you today?`;

/**
 * Default welcome buttons shown under the welcome message.
 */
const DEFAULT_WELCOME_BUTTONS = [
  { label: 'Book an appointment', message: "I'd like to book an appointment" },
  { label: 'Ask about services', message: 'What services do you offer?' },
  { label: 'Query opening hours', message: 'What are your hours?' },
];

/**
 * Build the default welcome message with business name inserted.
 * @param {string} [businessName] - Business display name (e.g. from platform API); falls back to FALLBACK_BUSINESS_NAME
 * @returns {string}
 */
function getDefaultWelcomeMessage(businessName) {
  const name = businessName || FALLBACK_BUSINESS_NAME;
  return DEFAULT_WELCOME_MESSAGE_TEMPLATE.split(WELCOME_MESSAGE_PLACEHOLDER).join(name);
}

const DEFAULT_CONFIG = {
  id: '',
  business_id: '',
  widget_title: FALLBACK_BUSINESS_NAME,
  faq_and_guidance: '',
  welcome_message: getDefaultWelcomeMessage(FALLBACK_BUSINESS_NAME),
  brand_color: '#3B82F6',
  /** Default text color (matches editor frame title color #333333). */
  text_color: '#333333',
  logo_url: '',
  actions: [],
  auto_popup_enabled: true,
  auto_popup_delay: 5,
  welcome_buttons: DEFAULT_WELCOME_BUTTONS,
  lead_capture_text: 'Please share your full name and contact information below in case we need to follow up with you.',
  button_text: 'Contact / FAQ',
  include_contact_info: true,
  include_services: true,
  include_products: true,
  include_booking_policies: true,
  include_terms_of_service: false,
  guidance_urls: [],
};

/**
 * Paths to edit guidance sources in the app (Angular/settings). Used for "edit" links next to checkboxes.
 * Values can be a path string or { path, queryParams } so Angular receives query params separately and
 * can merge them (e.g. with section=website-widgets) without producing a malformed URL.
 */
const GUIDANCE_EDIT_PATHS = {
  contact_info: '/app/settings/business',
  services: '/app/settings/services',
  products: '/app/settings/products',
  booking_policies: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
  terms_of_service: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
};

/** Calendar settings → General availability (same as Vuetage `navigateTo('app.calendar_settings', { tab: 'GeneralAvailability' })`). */
const CALENDAR_GENERAL_AVAILABILITY_SETTINGS = {
  path: '/app/settings/calendar_settings',
  queryParams: { tab: 'GeneralAvailability' },
};

export {
  DEFAULT_CONFIG,
  EMPTY_BUSINESS,
  getDefaultWelcomeMessage,
  DEFAULT_WELCOME_BUTTONS,
  GUIDANCE_EDIT_PATHS,
  CALENDAR_GENERAL_AVAILABILITY_SETTINGS,
};
